import React, { useEffect, useState } from "react";
import { Skull, Users, Swords, ArrowRight } from "lucide-react";
import { sfxEngage, sfxVictory, sfxDefeat } from "@/lib/audio";

/**
 * BATTLE REVEAL — cinematic reveal that plays before the stats appear.
 *
 * Flow:
 *   1. Black screen, single opening line typewriter-fades in
 *   2. Two more short beats reveal one at a time
 *   3. Outcome banner (VICTORY / DEFEAT / REPELLED) slams in with a shake or dim
 *   4. Key numbers count up
 *   5. CONTINUE button appears
 *
 * Props:
 *   result: the battle result object (needs .narrative, .committed, .zombie_casualties,
 *           .human_kills, .converted, .rejected, .narrative_summary)
 *   isPersonal: true → duel-mode language ("YOU LIVE" / "YOU FELL")
 *   onDismiss: called when user hits CONTINUE
 */
export default function BattleReveal({ result, isPersonal, onDismiss }) {
  const [step, setStep] = useState(0);   // 0=title, 1=beat1, 2=beat2, 3=outcome, 4=nums, 5=done
  const [shake, setShake] = useState(false);

  const won = isPersonal
    ? (result.outcome === "SURVIVAL" || result.outcome === "DOMINANCE")
    : (!result.rejected && (result.human_kills || 0) > (result.zombie_casualties || 0));
  const rejected = !!result.rejected;

  // Split narrative into 3 beats (by sentence). Trim to keep pace tight.
  const beats = React.useMemo(() => {
    const raw = String(result.narrative || result.narrative_summary || "").trim();
    if (!raw) return ["The dead answer.", "The living cannot.", "Silence returns to the field."];
    // Split on period/em-dash into sentences
    const sents = raw
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 4);
    if (sents.length < 3) return [sents.join(" "), ...Array(3 - sents.length).fill("")].slice(0, 3);
    // Pick 3 well-spaced sentences from the narrative
    return [sents[0], sents[Math.floor(sents.length / 2)], sents[sents.length - 1]];
  }, [result.narrative, result.narrative_summary]);

  // Auto-advance
  useEffect(() => {
    if (step >= 5) return;
    const delays = [900, 1400, 1400, 900, 1200];
    const t = setTimeout(() => {
      setStep((s) => s + 1);
      // Fire audio + shake right when the outcome banner lands (step 2→3)
      if (step === 2) {
        if (rejected || !won) sfxDefeat();
        else sfxVictory();
        if (won && !rejected) {
          setShake(true);
          setTimeout(() => setShake(false), 520);
        }
      }
      // Kick the engage-punch on the very first beat
      if (step === 0) sfxEngage();
    }, delays[step] || 900);
    return () => clearTimeout(t);
  }, [step, won, rejected]);

  const outcomeText = rejected
    ? "REPELLED"
    : isPersonal
      ? (won ? "YOU LIVE" : "YOU FELL")
      : (won ? "VICTORY" : "DEFEAT");
  const outcomeColor = rejected
    ? "text-blood"
    : won ? "text-[#d5c9b1]" : "text-blood";

  return (
    <div
      className={`fixed inset-0 z-[80] flex items-center justify-center px-6 py-10 bg-black ${
        shake ? "battle-reveal-shake" : ""
      }`}
      data-testid="battle-reveal"
    >
      {/* subtle grain overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
           style={{ backgroundImage: "url(data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22200%22 height=%22200%22 filter=%22url(%23n)%22/></svg>)" }} />

      <div className="max-w-2xl w-full text-center space-y-8 relative">
        {/* beats */}
        <div className="space-y-4 min-h-[120px]">
          {step >= 0 && (
            <p className="text-[0.65rem] font-mono tracking-[0.4em] text-blood battle-reveal-fade"
               data-testid="battle-reveal-title">
              — FIELD REPORT —
            </p>
          )}
          {step >= 1 && (
            <p className="font-serif-h text-xl lg:text-2xl text-bone italic leading-relaxed battle-reveal-fade"
               data-testid="battle-reveal-beat-1">
              {beats[0]}
            </p>
          )}
          {step >= 2 && beats[1] && (
            <p className="font-serif-h text-lg lg:text-xl text-bone/80 italic leading-relaxed battle-reveal-fade"
               data-testid="battle-reveal-beat-2">
              {beats[1]}
            </p>
          )}
        </div>

        {/* outcome banner */}
        {step >= 3 && (
          <div className="py-8 border-y border-blood/40 battle-reveal-slam"
               data-testid="battle-reveal-outcome">
            <p className={`font-serif-h text-6xl lg:text-7xl ${outcomeColor} tracking-wider`}>
              {outcomeText}
            </p>
            {beats[2] && (
              <p className="text-sm text-muted-h italic mt-4 max-w-lg mx-auto leading-relaxed">
                {beats[2]}
              </p>
            )}
          </div>
        )}

        {/* key numbers */}
        {step >= 4 && !rejected && (
          <div className="grid grid-cols-3 gap-4 text-center battle-reveal-fade"
               data-testid="battle-reveal-stats">
            <Metric icon={<Users className="w-3 h-3" />} label="COMMITTED"
                    value={result.committed || result.attacker_losses} />
            <Metric icon={<Skull className="w-3 h-3" />} label="FALLEN"
                    value={result.zombie_casualties || result.attacker_losses || 0}
                    tone="blood" />
            <Metric icon={<Swords className="w-3 h-3" />}
                    label={isPersonal ? "SLAIN" : "CONVERTED"}
                    value={isPersonal ? (result.human_kills || 0) : (result.converted || 0)}
                    tone="bone" />
          </div>
        )}

        {/* continue */}
        {step >= 5 && (
          <button
            onClick={onDismiss}
            className="btn-blood mx-auto inline-flex items-center gap-2 battle-reveal-fade"
            data-testid="battle-reveal-continue"
          >
            READ THE FULL REPORT <ArrowRight className="w-3 h-3" />
          </button>
        )}

        {/* skip */}
        {step < 5 && (
          <button
            onClick={() => setStep(5)}
            className="absolute top-0 right-0 -mt-6 -mr-2 text-[0.6rem] font-mono tracking-widest text-muted-h hover:text-bone opacity-50 hover:opacity-100"
            data-testid="battle-reveal-skip"
          >
            SKIP
          </button>
        )}
      </div>
    </div>
  );
}

function Metric({ icon, label, value, tone }) {
  const color = tone === "blood" ? "text-blood" : tone === "bone" ? "text-[#d5c9b1]" : "text-bone";
  return (
    <div className="border-l-2 border-[var(--border-subtle)] pl-3 py-2 text-left">
      <p className="text-[0.55rem] tracking-[0.2em] font-mono text-muted-h flex items-center gap-1">
        {icon} {label}
      </p>
      <p className={`text-3xl tabular-nums font-serif-h mt-1 ${color}`}>{value ?? 0}</p>
    </div>
  );
}
